import { SlideData } from './types';

export const DECK_SLIDES: SlideData[] = [
  {
    id: 'vision',
    number: 1,
    title: 'Zero-Trust Enterprise AI Sovereignty & In-Memory DLP Vault',
    subtitle: 'The infrastructure layer for sovereign enterprise AI adoption.',
    category: 'Title & Vision',
    notes: {
      title: 'Slide 1: Vision & Category Creation',
      talkingPoints: [
        'Welcome everyone. We are Workouse AI, building the zero-trust data sovereignty and financial governance layer for enterprise generative AI.',
        'Enterprises are eager to unleash the power of LLMs across their workforce, but security and compliance leaders are hitting the emergency brakes.',
        'We solve this at the network and runtime layer with an in-memory ephemeral DLP token vault that redacts PII before prompts leave the corporate firewall.',
      ],
      investorFaq: {
        question: 'Why do enterprises need an AI Gateway rather than just using Azure OpenAI / AWS Bedrock private endpoints?',
        answer: 'Private cloud endpoints do not prevent employee prompts from leaking customer PII, trade secrets, or credentials across internal departments, nor do they provide atomic per-department USD budget guardrails or multi-model portability.',
      },
      metricsToHighlight: ['Target Raise: $1.0M Seed', 'Market Category: Enterprise AI Sovereignty', 'Valuation Cap: $8.0M SAFE'],
    },
  },
  {
    id: 'problem',
    number: 2,
    title: 'The Enterprise AI Paradox: Adoption vs. Risk',
    subtitle: 'Enterprises desperately need GenAI, but CISOs & CFOs are forced to block it.',
    category: 'The Problem',
    notes: {
      title: 'Slide 2: The Enterprise Pain Points',
      talkingPoints: [
        'Over 78% of enterprise CISOs have banned or restricted employee access to public AI tools due to data exfiltration fears.',
        'Simultaneously, engineering teams face 3x budget blowouts because LLM token consumption is non-deterministic.',
        'The average data breach costs $4.45M, and new regulations like the EU AI Act impose massive fines for non-compliant AI data processing.',
      ],
      investorFaq: {
        question: 'Is data loss prevention really the main friction in enterprise AI?',
        answer: 'Yes. In regulated industries (finance, healthcare, legal, defense), a single leaked prompt with customer PII or confidential IP can trigger catastrophic regulatory fines and reputational loss.',
      },
      metricsToHighlight: ['78% of CISOs restrict AI', '$4.45M avg breach cost', '3.4x budget overshoots'],
    },
  },
  {
    id: 'solution',
    number: 3,
    title: 'The Workouse Gateway: Zero-Trust by Design',
    subtitle: 'A drop-in, sub-millisecond security & cost governor between workforce and any LLM.',
    category: 'The Solution',
    notes: {
      title: 'Slide 3: The Workouse Solution',
      talkingPoints: [
        'Workouse is an OpenAI-compatible proxy that drops into any existing SDK, LangChain app, or browser workflow.',
        'When a request arrives, our in-memory DLP Token Vault replaces sensitive entities with synthetic tokens in RAM (< 1.2ms latency).',
        'Only sanitized tokens reach the upstream LLM. Upon response return, the tokens are dynamically rehydrated in-memory and instantly released.',
      ],
      investorFaq: {
        question: 'Does tokenization degrade the LLM response quality or reasoning?',
        answer: 'No. Our tokenizer preserves syntactic and semantic structure (e.g. replacing a name with [PERSON_01] and an email with [EMAIL_01]), allowing the LLM to reason accurately without knowing the true underlying identity.',
      },
      metricsToHighlight: ['< 1.2ms RAM Tokenization', 'Zero raw text retention', '100% OpenAI API compatible'],
    },
  },
  {
    id: 'tech-moat',
    number: 4,
    title: 'Engineering Excellence & Architecture Purity',
    subtitle: 'Built from first principles for high-throughput security and zero memory leaks.',
    category: 'Deep Tech Moat',
    notes: {
      title: 'Slide 4: Technical Moat & Architecture',
      talkingPoints: [
        'Our technical moat spans 6 deep architectural pillars: Ephemeral In-Memory DLP Vault, Isolated V8 Sandboxes with HITL dry-run diffs, Authoritative HashiCorp Vault Secrets, WAP Connector Plugins, Haskell Parsec async document workers, and Enterprise SAML/LDAP IAM.',
        'We enforce strict functional programming contracts across TypeScript domain packages, providing mathematical type safety and zero unhandled exceptions.',
        'Our Two-Phase Human-in-the-Loop sandbox simulates state-mutating actions first, rendering visual diffs in chat before committing live execution.',
      ],
      investorFaq: {
        question: 'How do you prevent malicious or hallucinated LLM tool calls from damaging enterprise systems?',
        answer: 'All tool calls execute inside isolated V8 Dynamic Dispatch or Deno sandboxes. State mutations run in dryRun mode first and generate interactive diffs for human approval. Furthermore, secrets are broker-proxied via HashiCorp Vault, so untrusted code never sees raw credentials.',
      },
      metricsToHighlight: ['Isolated V8 & Deno Sandboxes', 'HashiCorp Vault Integration', 'WAP Plugin Ecosystem'],
    },
  },
  {
    id: 'market',
    number: 5,
    title: 'A $35B+ Market with Urgent Regulatory Tailwinds',
    subtitle: 'Enterprise AI security is transitioning from optional experiment to mandatory CISO compliance.',
    category: 'Market Opportunity',
    notes: {
      title: 'Slide 5: Market Opportunity & TAM',
      talkingPoints: [
        'The Enterprise AI Governance, Security, and Compliance market is expanding at a 42.6% CAGR, reaching $35.4B by 2030.',
        'Our initial serviceable market (SAM) is $8.5B in enterprise gateways, LLM proxies, and FinOps accounting.',
        'Our beachhead SOM is $450M, focused on regulated mid-market and enterprise organizations in finance, healthcare, legal, and defense.',
      ],
      investorFaq: {
        question: 'How fast is enterprise spending on AI security growing?',
        answer: 'Gartner reports that 82% of enterprise security leaders have dedicated GenAI security line items in their 2026/2027 IT budgets.',
      },
      metricsToHighlight: ['$35.4B TAM by 2030', '+42.6% CAGR', '$450M Initial SOM Beachhead'],
    },
  },
  {
    id: 'business-model',
    number: 6,
    title: 'Dual-Engine Monetization & High Gross Margins',
    subtitle: 'Capturing high-ACV enterprise contracts while driving bottom-up SaaS developer adoption.',
    category: 'Business Model',
    notes: {
      title: 'Slide 6: Business Model & Pricing',
      talkingPoints: [
        'We operate a dual-engine monetization model:',
        '1. Self-Hosted Enterprise Licenses: $24k - $120k ARR per client based on nodes, SAML/LDAP SSO, SLA, and custom DLP rules.',
        '2. Managed Cloud SaaS: $99 - $499/mo subscription via LemonSqueezy with metered usage overage.',
        'We achieve 88%+ software gross margins and anticipate 135%+ Net Revenue Retention as enterprise AI request volume naturally scales.',
      ],
      investorFaq: {
        question: 'What drives your Net Revenue Retention (NRR) above 130%?',
        answer: 'As enterprise clients expand their internal AI applications and autonomous agents, their token throughput and node count increase, automatically expanding contract value without additional sales cycles.',
      },
      metricsToHighlight: ['88%+ Gross Margins', '$24k–$120k ACV', '135%+ Net Revenue Retention'],
    },
  },
  {
    id: 'competition',
    number: 7,
    title: 'Why Workouse Wins: Complete Zero-Trust Moat',
    subtitle: 'Other AI gateways are just routing proxies. Workouse is a complete Data Sovereignty & Financial Governor.',
    category: 'Competitive Advantage',
    notes: {
      title: 'Slide 7: Competitive Landscape',
      talkingPoints: [
        'Existing gateways like Portkey, Cloudflare AI Gateway, and LiteLLM focus primarily on caching and routing, but lack bi-directional in-memory DLP token vaults and isolated execution sandboxes.',
        'Legacy DLP tools like Netskope/Zscaler are built for static web traffic and do not understand tokenized LLM turn contexts, prompt rehydration, or agentic tool calls.',
        'Workouse is the only platform providing in-memory bi-directional DLP, isolated V8 sandboxes with HITL dry-run diffs, HashiCorp Vault secrets isolation, and air-gapped on-prem parity.',
      ],
      investorFaq: {
        question: 'What stops Cloudflare or OpenAI from copying this?',
        answer: 'Cloudflare and OpenAI are upstream cloud vendors; enterprises specifically do not want to trust cloud vendors with raw sensitive data. Workouse runs on the enterprise edge or on-prem with strict zero-knowledge architecture and authoritative secret stores.',
      },
      metricsToHighlight: ['In-Memory DLP Vault', 'Isolated V8 & HITL Diffs', 'HashiCorp Vault Integration'],
    },
  },
  {
    id: 'traction-gtm',
    number: 8,
    title: 'Go-To-Market & Developer-First Adoption',
    subtitle: 'Combining frictionless open-core developer distribution with top-down enterprise CISO sales.',
    category: 'Traction & GTM',
    notes: {
      title: 'Slide 8: Go-To-Market Execution',
      talkingPoints: [
        'Our GTM is built on a 3-stage land-and-expand funnel:',
        'Bottom-up: Developers discover our open-core GitHub repository and run self-hosted Docker containers locally.',
        'Mid-tier: Teams connect credit cards via LemonSqueezy for central key management and budget caps.',
        'Top-down: CISOs mandate Workouse for enterprise-wide SAML 2.0 SSO, audit logs, and compliance enforcement.',
      ],
      investorFaq: {
        question: 'What is your early pipeline and waitlist traction?',
        answer: 'Our landing page and waitlist engine are live on Cloudflare edge, actively enrolling design partner pilot candidates in fintech, healthcare, and B2B SaaS for the Q3 private beta.',
      },
      metricsToHighlight: ['Open-Core GitHub Funnel', 'Private Beta Design Partners', 'Target: Fintech, Healthcare, Legal'],
    },
  },
  {
    id: 'roadmap',
    number: 9,
    title: '18-Month Product & Growth Milestones',
    subtitle: 'Disciplined technical milestones designed to achieve $1.2M ARR and enterprise compliance certification.',
    category: 'Execution Roadmap',
    notes: {
      title: 'Slide 9: Product & Growth Roadmap',
      talkingPoints: [
        'Q3 2026: Private Beta cohort with 15 design partners, Cloudflare edge multi-tenant gateway, LemonSqueezy billing, and WAP core connector plugins (Paraşüt & CRM).',
        'Q4 2026: Isolated V8 dynamic dispatch & Deno sandbox runners, Two-Phase HITL dry-run visual diff approval, and Saved Reusable Workflow Apps with slash commands.',
        'Q1 2027: Enterprise HashiCorp Vault on-prem integration, SOC 2 Type I and ISO 27001 audit completion, and air-gapped defense/banking container packages.',
        'Q2-Q4 2027: Autonomous agent tool-call DLP & spend arbitration, reaching $1.2M ARR across 50+ enterprise orgs.',
      ],
      investorFaq: {
        question: 'What is the highest risk milestone on this roadmap?',
        answer: 'Achieving SOC 2 Type I and enterprise procurement cycles; we have budgeted dedicated capital and advisory support to accelerate compliance audits within Q1 2027.',
      },
      metricsToHighlight: ['Q3 2026: WAP Connectors', 'Q4 2026: V8 Sandboxes & HITL', 'Q1 2027: HashiCorp Vault & SOC 2'],
    },
  },
  {
    id: 'team',
    number: 10,
    title: 'Engineering DNA: Systems, Security & Purity',
    subtitle: 'A deeply technical team with strong foundations in functional programming and enterprise security.',
    category: 'The Team',
    notes: {
      title: 'Slide 10: Team & DNA',
      talkingPoints: [
        'We are an engineering-led team with deep expertise in functional programming (Haskell / TypeScript FP), distributed systems, and enterprise IAM.',
        'We build product-grade systems from day one, with zero disposable prototypes or architectural debt.',
        'With this seed round, we are expanding our core systems engineering and developer relations bench.',
      ],
      investorFaq: {
        question: 'How do you ensure you can recruit top-tier systems talent?',
        answer: 'Top functional programmers and systems engineers are attracted to our Haskell/TypeScript FP purity standards and open-core community traction.',
      },
      metricsToHighlight: ['Haskell & TypeScript FP', 'Enterprise IAM & SAML Depth', 'Lean & High Velocity'],
    },
  },
  {
    id: 'the-ask',
    number: 11,
    title: 'Raising $1.0M Seed to Capture Enterprise AI Security',
    subtitle: '18 months of disciplined runway to achieve $1.2M ARR, SOC 2 certification, and 50+ enterprise deployments.',
    category: 'The Investment Ask',
    notes: {
      title: 'Slide 11: The Ask & Use of Proceeds',
      talkingPoints: [
        'We are raising $1,000,000 in Seed funding (SAFE / Convertible) with an 18-month runway.',
        '60% ($600k) is allocated to Core Engineering & R&D (DLP Token Vault optimizations, agentic security).',
        '25% ($250k) to Go-To-Market, DevRel, and enterprise sales pilot onboarding.',
        '15% ($150k) to SOC 2 / ISO 27001 compliance audits and infrastructure.',
        'Our target is to exit this runway at $1.2M ARR with 50+ paid enterprise deployments and clear Series A readiness.',
      ],
      investorFaq: {
        question: 'What will be the valuation and terms for this round?',
        answer: 'We are raising on a standard Post-Money SAFE with an $8.0M valuation cap, welcoming value-add lead and syndicate investors in cybersecurity, AI infra, and enterprise software.',
      },
      metricsToHighlight: ['Target: $1.0M Raise', '18 Months Runway', 'Goal: $1.2M ARR & 50+ Orgs'],
    },
  },
  {
    id: 'contact',
    number: 12,
    title: 'Join Us in Securing the Enterprise AI Frontier',
    subtitle: 'We are actively engaging with visionary investors who understand the critical infrastructure layer of enterprise AI.',
    category: 'Next Steps & Contact',
    notes: {
      title: 'Slide 12: Next Steps & Meeting Action',
      talkingPoints: [
        'Thank you for your time. We invite you to schedule a 20-minute investment memo deep dive and live DLP benchmark walkthrough.',
        'Reach us directly at investors@workouse.com or explore our live platform at ai-gateway.workouse.com.',
        'We welcome your questions and look forward to partnering together.',
      ],
      investorFaq: {
        question: 'Can we test the live gateway before finalizing terms?',
        answer: 'Absolutely. We provide immediate sandbox access and live token sanitization benchmarks upon request.',
      },
      metricsToHighlight: ['investors@workouse.com', 'ai-gateway.workouse.com', 'github.com/workouse'],
    },
  },
];
